import { OnboardingTour } from "./types";

export const USERS_ONBOARDING_TOUR_ID = "users-dashboard-tour";

/**
 * Onboarding step field guide:
 * - `id`: Stable identifier used for saved progress and action handlers.
 * - `route` / `routePrefix`: Where the step is eligible to render.
 * - `target`: CSS selector for the element the bubble points to.
 * - `placement`: Bubble position relative to its target; `offset` fine-tunes it.
 * - `highlightPadding`, `showOverlay`, `showHighlight`, and `nonBlocking`: Control
 *   how the target is visually emphasized and whether surrounding UI stays usable.
 * - `waitForTargetScroll`: Keep the bubble hidden until its target has scrolled
 *   into view.
 * - `buttons`: Available actions. `next` advances normally; `custom` runs the
 *   matching handler registered by the relevant page.
 * - `transitionDelayMs`: Override the default delay before a `next` action advances.
 * - `actionDelayMs`: Wait before a custom action runs (for example, opening a modal).
 * - `completionDelayMs`: Run a custom action immediately, then wait before moving
 *   onboarding forward so the destination page can render its target.
 */
export const usersOnboardingTour: OnboardingTour = {
    id: USERS_ONBOARDING_TOUR_ID,
    version: 1,
    steps: [
        // Step 1: Welcome
        {
            id: "users-welcome",
            route: "/",
            title: "You are one minute away",
            message: "Set up your first client and get familiar with the core workflow from here.",
            buttons: [
                {
                    id: "users-welcome-cta",
                    label: "Let's Go",
                    action: "next",
                    variant: "contained",
                    color: "primary",
                },
            ],
        },
        // Step 2: Add a client
        {
            id: "users-add-client",
            route: "/",
            title: "Add your first client",
            message: "Use Add Client to invite someone into your dashboard or start their onboarding manually.",
            target: {
                selector: '[data-onboarding="users-add-client"]',
            },
            placement: {
                anchorOrigin: {
                    vertical: "top",
                    horizontal: "left",
                },
                transformOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                },
                offset: {
                    y: -6,
                    x: 20,
                },
            },
            highlightPadding: 10,
            resumeCondition: "resume-current-step",
            buttons: [
                {
                    id: "users-add-client-open",
                    label: "Add Client",
                    action: "custom",
                    actionId: "open-add-client-modal",
                    actionDelayMs: 500,
                    variant: "contained",
                    color: "primary",
                },
            ],
        },
        // Step 3: Add-yourself tip
        {
            id: "users-add-client-modal-tip",
            route: "/",
            title: "Pro Tip: Add Yourself as a Client",
            message:
                "Add yourself first to experience the app from the same perspective as your clients. It is the fastest way to understand the flow.",
            target: {
                selector: '[data-onboarding="manual-add-client-modal"]',
            },
            placement: {
                anchorOrigin: {
                    vertical: "top",
                    horizontal: "right",
                },
                transformOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                },
                offset: {
                    y: -16,
                    x: 20,
                },
                // Desktop: bubble sits beside the modal. On narrow screens the flip
                // modifier falls back to top/bottom automatically.
                popperPlacement: "right-start",
            },
            showOverlay: false,
            showHighlight: true,
            nonBlocking: true,
            buttons: [
                {
                    id: "users-add-client-modal-tip-got-it",
                    label: "Got it",
                    action: "custom",
                    actionId: "hide-add-client-modal-tip",
                    variant: "contained",
                    color: "primary",
                },
            ],
        },
        // Step 4: Create first Ziv
        {
            id: "pending-client-create-first-ziv",
            route: "/pending-client",
            routePrefix: "/pending-client/",
            title: "Create your first Ziv",
            message: "Start by creating the first pre-configured Ziv for this client. This is where you define the questions they will receive.",
            target: {
                selector: '[data-onboarding="pending-client-new-ziv"]',
            },
            placement: {
                anchorOrigin: {
                    vertical: "top",
                    horizontal: "left",
                },
                transformOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                },
                offset: {
                    y: -5,
                    x: -20,
                },
            },
            highlightPadding: 7,
            buttons: [
                {
                    id: "pending-client-open-new-ziv",
                    label: "New Ziv",
                    action: "custom",
                    actionId: "open-pending-client-new-ziv-modal",
                    actionDelayMs: 500,
                    variant: "contained",
                    color: "primary",
                },
            ],
        },
        // Step 5: Include Ziv in score
        {
            id: "pending-client-ziv-track-what-counts",
            route: "/pending-client",
            routePrefix: "/pending-client/",
            title: "Track What Counts",
            message:
                "Turn this on to include responses in your client's overall Ziv Score, a snapshot of their engagement and consistency over time.",
            target: {
                selector: '[data-onboarding="pending-ziv-include-in-average"]',
            },
            placement: {
                anchorOrigin: {
                    vertical: "top",
                    horizontal: "right",
                },
                transformOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                },
            },
            showOverlay: false,
            showHighlight: true,
            highlightPadding: 4,
            buttons: [
                {
                    id: "pending-client-ziv-track-what-counts-got-it",
                    label: "Got it",
                    action: "custom",
                    actionId: "defer-pending-client-belief-score",
                    //actionDelayMs: 500,
                    variant: "contained",
                    color: "primary",
                    //completionDelayMs: 500,
                },
            ],
        },
        // Step 6: Explain Belief Score
        {
            id: "pending-client-belief-score",
            route: "/pending-client",
            routePrefix: "/pending-client/",
            title: "Belief Score",
            message:
                "This question helps surface one of the most important client signals. Use it to track whether therapy still feels helpful and spot when someone may need attention sooner.",
            target: {
                selector: '[data-onboarding="pending-client-belief-score"]',
            },
            placement: {
                anchorOrigin: {
                    vertical: "top",
                    horizontal: "left",
                },
                transformOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                },
                offset: {
                    y: -5,
                },
            },
            highlightPadding: 10,
            buttons: [
                {
                    id: "pending-client-belief-score-got-it",
                    label: "Got it",
                    action: "custom",
                    actionId: "go-to-users-belief-score",
                    actionDelayMs: 500,
                    variant: "contained",
                    color: "primary",
                },
            ],
        },
        // Step 7: Belief Score dashboard column
        {
            id: "users-belief-score-header",
            route: "/",
            title: "One of the Most Important Signals",
            message:
                "Back on your dashboard, this Belief Score column helps you quickly spot whether a client still feels therapy is helping. It is one of the clearest signals to monitor over time.",
            target: {
                selector: '[data-onboarding="users-belief-score-header"]',
            },
            placement: {
                anchorOrigin: {
                    vertical: "top",
                    horizontal: "left",
                },
                transformOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                },
                offset: {
                    y: -5,
                    x: 5,
                },
            },
            highlightPadding: 10,
            buttons: [
                {
                    id: "users-belief-score-header-got-it",
                    label: "Got it",
                    action: "next",
                    variant: "contained",
                    color: "primary",
                },
            ],
        },
        // Step 8: Send a client message
        {
            id: "users-send-message",
            route: "/",
            title: "Message Clients Directly",
            message: "Send a quick one-way message from here to check in, encourage progress, or follow up without leaving the dashboard.",
            target: {
                selector: '[data-onboarding="users-send-message"]',
            },
            placement: {
                anchorOrigin: {
                    vertical: "top",
                    horizontal: "left",
                },
                transformOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                },
                offset: {
                    y: -5,
                    x: 15,
                },
            },
            highlightPadding: 10,
            buttons: [
                {
                    id: "users-send-message-got-it",
                    label: "Got it",
                    action: "next",
                    variant: "contained",
                    color: "primary",
                },
            ],
        },
        // Step 9: Filter dashboard dates
        {
            id: "users-date-filter",
            route: "/",
            title: "Filter by Time Period",
            message:
                "Adjust the date range to review any window of client activity. It is useful for session prep, trend checks, and progress reviews over time.",
            target: {
                selector: '[data-onboarding="users-date-filter"]',
            },
            placement: {
                anchorOrigin: {
                    vertical: "top",
                    horizontal: "left",
                },
                transformOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                },
                offset: {
                    y: -5,
                    x: -15,
                },
            },
            highlightPadding: 10,
            buttons: [
                {
                    id: "users-date-filter-got-it",
                    label: "Got it",
                    action: "custom",
                    actionId: "go-to-settings-practitioner-code",
                    completionDelayMs: 250,
                    variant: "contained",
                    color: "primary",
                },
            ],
        },
        // Step 10: Find practitioner code
        {
            id: "settings-practitioner-code",
            route: "/settings",
            title: "Your Practitioner Code",
            message: "This is your unique practitioner code. Clients can also register under your practice using this code.",
            target: {
                selector: '[data-onboarding="settings-practitioner-code"]',
            },
            placement: {
                anchorOrigin: {
                    vertical: "top",
                    horizontal: "left",
                },
                transformOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                },
                offset: {
                    y: -3,
                    x: 30,
                },
            },
            highlightPadding: 10,
            buttons: [
                {
                    id: "settings-practitioner-code-got-it",
                    label: "Got it",
                    action: "next",
                    transitionDelayMs: 300,
                    variant: "contained",
                    color: "primary",
                },
            ],
        },
        // Step 11: Complete onboarding
        {
            id: "settings-onboarding-complete",
            route: "/settings",
            title: "You’re All Set",
            message: "You’ve completed the core walkthrough. You can come back here anytime to restart onboarding and revisit the product tour.",
            target: {
                selector: '[data-onboarding="settings-restart-onboarding"]',
            },
            placement: {
                anchorOrigin: {
                    vertical: "top",
                    horizontal: "right",
                },
                transformOrigin: {
                    vertical: "bottom",
                    horizontal: "right",
                },
                offset: {
                    y: -8,
                },
            },
            highlightPadding: 10,
            waitForTargetScroll: true,
            buttons: [
                {
                    id: "settings-onboarding-complete-got-it",
                    label: "Got it",
                    action: "custom",
                    actionId: "complete-tour-go-home",
                    variant: "contained",
                    color: "primary",
                },
            ],
        },
    ],
};

export const onboardingTours: Record<string, OnboardingTour> = {
    [usersOnboardingTour.id]: usersOnboardingTour,
};
